import { entityIcon } from "@/components/ActivityFeed";
import { type Status, StatusIcon } from "@/components/StatusIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { ActivityService } from "@/gen/omnios/v1/activity_connect";
import type { ActivityEntry } from "@/gen/omnios/v1/activity_pb";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { formatDate } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";
import { createClient } from "@connectrpc/connect";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const client = createClient(ActivityService, transport);

const ACTION_MAP: Record<string, string> = {
	"product.created": "action_product_created",
	"product.updated": "action_product_updated",
	"product.deleted": "action_product_deleted",
	"category.created": "action_category_created",
	"category.updated": "action_category_updated",
	"category.deleted": "action_category_deleted",
	"member.invited": "action_member_invited",
	"member.removed": "action_member_removed",
	"role.created": "action_role_created",
	"work_item.created": "action_work_item_created",
	"work_item.updated": "action_work_item_updated",
	"work_item.status_changed": "action_work_item_status_changed",
	"work_item.deleted": "action_work_item_deleted",
	"party.created": "action_party_created",
	"party.updated": "action_party_updated",
	"party.deleted": "action_party_deleted",
	"party.comment": "action_comment",
	"order.created": "action_order_created",
	"order.updated": "action_order_updated",
	"order.deleted": "action_order_deleted",
	"member.profileupdated": "action_profile_updated",
	"workspace_member.created": "action_member_created",
	"store_config.created": "action_store_config_created",
	"portal_instance.created": "action_portal_created",
	"price_list.activated": "action_activated",
	"price_list.deactivated": "action_deactivated",
	"promotion_rule.activated": "action_activated",
	"promotion_rule.deactivated": "action_deactivated",
	"promotion_rule.created": "action_promotion_created",
	"workspace.created": "action_workspace_created",
	"execution_run.runcompleted": "action_runcompleted",
	"execution_run.runstarted": "action_runstarted",
	"execution_run.progressupdated": "action_progressupdated",
	"execution_run.stepcompleted": "action_stepcompleted",
	"pipeline_run.created": "action_pipeline_run_created",
	"pipeline.created": "action_pipeline_created",
};

const ENTITY_LABEL_KEYS: Record<string, string> = {
	product: "entity_product",
	category: "entity_category",
	work_item: "entity_work_item",
	order: "entity_order",
	party: "entity_party",
	store_config: "entity_store_config",
	portal_instance: "entity_portal",
	price_list: "entity_price_list",
	promotion_rule: "entity_promotion_rule",
	workspace_member: "entity_workspace_member",
	workspace: "entity_workspace",
	execution_run: "entity_execution_run",
	pipeline_run: "entity_pipeline_run",
	pipeline: "entity_pipeline",
};

/** Map the trailing verb of an action to a Linear-style StatusIcon status.
 *  Activity entries are historical — never use "running" (spinning animation). */
function actionToStatus(action: string): Status {
	const verb = (action.split(".").pop() ?? action).toLowerCase();
	switch (verb) {
		case "created":
		case "completed":
		case "runcompleted":
		case "stepcompleted":
		case "activated":
		case "published":
			return "completed";
		case "deleted":
		case "removed":
		case "failed":
		case "cancelled":
			return "failed";
		case "deactivated":
		case "archived":
			return "skipped";
		default:
			return "pending";
	}
}

const ENTITY_TYPE_CONFIG: Record<
	string,
	{ color: string }
> = {
	product: { color: "text-blue-400" },
	category: { color: "text-blue-400" },
	work_item: { color: "text-purple-400" },
	order: { color: "text-green-400" },
	party: { color: "text-orange-400" },
	execution_run: { color: "text-indigo-400" },
	pipeline_run: { color: "text-indigo-400" },
	pipeline: { color: "text-indigo-400" },
	store_config: { color: "text-zinc-400" },
	portal_instance: { color: "text-cyan-400" },
	price_list: { color: "text-emerald-400" },
	promotion_rule: { color: "text-amber-400" },
	workspace_member: { color: "text-orange-400" },
	workspace: { color: "text-zinc-400" },
};

/** Extract a human-readable title from the activity entry payload. */
function extractTitle(entry: ActivityEntry): string {
	const fields = entry.payload?.fields;
	if (!fields) return "";
	// Try common title fields in order of preference
	for (const key of ["title", "name", "display_name", "label", "subject", "pipeline_name"]) {
		const val = fields[key]?.kind;
		if (val?.case === "stringValue" && val.value) return val.value as string;
	}
	// For email/username fields
	for (const key of ["email", "username"]) {
		const val = fields[key]?.kind;
		if (val?.case === "stringValue" && val.value) return val.value as string;
	}
	return "";
}

function getActivityHref(entry: ActivityEntry): string | null {
	switch (entry.entityType) {
		case "product":
			return entry.entityId
				? `/catalog/products/${entry.entityId}`
				: "/catalog/products";
		case "category":
			return "/catalog/categories";
		case "work_item":
			return entry.entityId ? `/work-items/${entry.entityId}` : "/work-items";
		case "party":
			return entry.entityId ? `/parties/${entry.entityId}` : "/parties";
		case "order":
			return entry.entityId
				? `/commerce/orders/${entry.entityId}`
				: "/commerce/orders";
		case "execution_run":
		case "pipeline_run":
			return entry.entityId
				? `/background-jobs/${entry.entityId}`
				: "/background-jobs";
		case "pipeline":
			return "/background-jobs";
		default:
			return null;
	}
}

interface ActivitySearch {
	entityType?: string;
	entityId?: string;
}

export const Route = createFileRoute("/activity")({
	validateSearch: (search: Record<string, unknown>): ActivitySearch => ({
		entityType: search.entityType as string | undefined,
		entityId: search.entityId as string | undefined,
	}),
	component: ActivityPage,
});

function ActivityPage() {
	const { entityType, entityId } = Route.useSearch();
	const navigate = useNavigate();
	const { t } = useTranslation("activity");
	const { onGridReady, onStateChanged } = useGridLayout("activity");
	const { selectedRows, hasSelection, onSelectionChanged, ActionBar } =
		useGridSelection<ActivityEntry>();

	const isFiltered = !!(entityType && entityId);

	useBreadcrumbs(
		isFiltered
			? [
					{
						label: t("title"),
						href: "/activity",
					},
					{
						label: `${t(ENTITY_LABEL_KEYS[entityType] ?? entityType)} ${entityId.slice(0, 8)}…`,
					},
				]
			: [{ label: t("title") }],
	);

	const extraFilter = useMemo(
		() => (isFiltered ? { entityType, entityId } : undefined),
		[isFiltered, entityType, entityId],
	);

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<ActivityEntry>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await client.listActivity({
				page: { pageSize, pageToken },
			});
			return {
				rows: (resp.entries ?? []) as ActivityEntry[],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		extraFilter,
		colToField: {
			entityType: "entity_type",
			entityId: "entity_id",
			time: "created_at",
		},
	});

	// ── AG Grid column definitions ──
	const columnDefs = useMemo<ColDef<ActivityEntry>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				colId: "entityType",
				headerName: t("col_type", { defaultValue: "Тип" }),
				width: 140,
				minWidth: 100,
				filter: false,
				cellRenderer: (p: { data: ActivityEntry | undefined }) => {
					if (!p.data) return null;
					const key = ENTITY_LABEL_KEYS[p.data.entityType];
					const typeLabel = key ? t(key) : p.data.entityType;
					const Icon = entityIcon(p.data.entityType);
					const cfg = ENTITY_TYPE_CONFIG[p.data.entityType];
					return (
						<span className="inline-flex items-center gap-1.5 leading-[28px]">
							<Icon className={`h-3.5 w-3.5 shrink-0 ${cfg?.color ?? "text-muted-foreground"}`} />
							<span className="text-muted-foreground">{typeLabel}</span>
						</span>
					);
				},
			},
			{
				colId: "title",
				headerName: t("col_title", { defaultValue: "Название" }),
				flex: 1,
				minWidth: 140,
				filter: false,
				cellRenderer: (p: { data: ActivityEntry | undefined }) => {
					if (!p.data) return null;
					const title = extractTitle(p.data);
					if (!title) return <span className="text-muted-foreground/50 leading-[28px]">—</span>;
					const href = getActivityHref(p.data);
					if (href) {
						return (
							<a
								href={href}
								className="text-foreground hover:underline leading-[28px] truncate"
								title={title}
								onClick={(e) => e.stopPropagation()}
							>
								{title}
							</a>
						);
					}
					return (
						<span className="text-foreground leading-[28px] truncate">{title}</span>
					);
				},
			},
			{
				colId: "entityId",
				headerName: "ID",
				width: 100,
				minWidth: 80,
				filter: false,
				cellRenderer: (p: { data: ActivityEntry | undefined }) => {
					if (!p.data?.entityId) return null;
					return (
						<span
							className="font-mono text-xs text-muted-foreground leading-[28px] truncate"
							title={p.data.entityId}
						>
							{p.data.entityId.slice(0, 8)}
						</span>
					);
				},
			},
			{
				colId: "event",
				headerName: t("col_action", { defaultValue: "Действие" }),
				width: 180,
				minWidth: 120,
				filter: false,
				cellRenderer: (p: { data: ActivityEntry | undefined }) => {
					if (!p.data) return "";
					const labelKey = ACTION_MAP[p.data.action];
					let label: string;
					if (labelKey) {
						label = t(`common:${labelKey}`);
					} else {
						const verb = (
							p.data.action.split(".").pop() ?? p.data.action
						).toLowerCase();
						label = t(`action_${verb}`, { defaultValue: verb });
					}
					const status = actionToStatus(p.data.action);
					return (
						<span className="inline-flex items-center gap-1.5 leading-[28px]">
							<StatusIcon status={status} size={14} />
							<span className="text-muted-foreground">{label}</span>
						</span>
					);
				},
			},
			{
				colId: "time",
				headerName: t("time"),
				flex: 1,
				minWidth: 160,
				filter: "agDateColumnFilter",
				valueGetter: (p) => p.data?.createdAt?.toDate()?.toISOString(),
				valueFormatter: (p) =>
					p.value
						? formatDate(new Date(p.value), "dd MMM yyyy HH:mm")
						: "\u2014",
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: ActivityEntry | undefined }) => {
			if (e.data) {
				const href = getActivityHref(e.data);
				if (href) {
					navigate({ to: href });
				}
			}
		},
		[navigate],
	);

	// ── Status bar ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: hasSelection
					? `${selectedRows.length} ${t("selected_label", { defaultValue: "выбрано" })}`
					: `${totalRows} ${t("total_label", { defaultValue: "записей" })}`,
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск по активности..." })}
						className="pl-9 h-9"
						onChange={(e) => {
							gridRef.current?.api?.setGridOption("quickFilterText", e.target.value);
						}}
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => refreshGrid()}
				>
					{t("refresh", { defaultValue: "Обновить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({ fileName: "activity.csv" })
					}
				>
					{t("export", { defaultValue: "Экспорт" })}
				</Button>
			</div>
			{isFiltered && (
				<div className="px-4 py-2 flex items-center gap-2">
					<Badge
						variant="secondary"
						className="gap-1 cursor-pointer"
						onClick={() => navigate({ to: "/activity", search: {} })}
					>
						{(() => {
							const Icon = entityIcon(entityType);
							return <Icon className="h-3 w-3" />;
						})()}
						{t(ENTITY_LABEL_KEYS[entityType] ?? entityType)}{" "}
						{entityId.slice(0, 8)}…
						<X className="h-3 w-3 ml-1" />
					</Badge>
				</div>
			)}
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<ActivityEntry>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onRowClicked={onRowClicked}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					rowClass="cursor-pointer"
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("no_activity")}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading")}...</span>`}
				/>
			</div>
			<ActionBar />
		</div>
	);
}
